import { SettingOutlined } from "@ant-design/icons";
import { Fragment, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Modal } from "src/Components";
import {
  ICCOrderImbalanceConfig,
  ICCOrderImbalanceRule,
  ICCOrderImbalanceStage,
} from "src/Interfaces/ccOrder";
import { DeltaHedgeOrderType } from "src/Interfaces/deltaHedgeOrder";
import {
  getCCOrderImbalanceConfig,
  setCCOrderImbalanceConfig,
} from "src/Requests/ccOrder";
import { getCCOrderCriterionTypeName } from "src/Utils/ccOrderCriterionTypes";
import { packetErrorHandler } from "src/Utils/requestUtils";

const models = [
  { id: DeltaHedgeOrderType.Open, title: "ورود" },
  { id: DeltaHedgeOrderType.Roll, title: "رول" },
  { id: DeltaHedgeOrderType.Offset, title: "آفست" },
];
const stageTitles = ["مرحله اول", "مرحله دوم", "مرحله سوم"];

const ImbalanceSetting = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [config, setConfig] = useState<ICCOrderImbalanceConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const controller = new AbortController();
    setLoading(true);
    setLoadFailed(false);
    setConfig(null);
    setDirty(false);
    getCCOrderImbalanceConfig({ signal: controller.signal })
      .then((response) => {
        if (!controller.signal.aborted) setConfig(response);
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        setLoadFailed(true);
        packetErrorHandler(error);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [isOpen, reloadKey]);

  const changeStage = (
    row: ICCOrderImbalanceRule,
    stageIndex: number,
    field: keyof ICCOrderImbalanceStage,
    value: string,
  ) => {
    setConfig((current) => current && ({
      ...current,
      rows: current.rows.map((item) =>
        item.orderType === row.orderType && item.criterionType === row.criterionType
          ? {
              ...item,
              stages: item.stages.map((stage, index) =>
                index === stageIndex
                  ? { ...stage, [field]: value === "" ? null : Number(value) }
                  : stage,
              ),
            }
          : item,
      ),
    }));
    setDirty(true);
  };

  const save = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!config?.canManage || saving || !dirty || !valid) return;
    setSaving(true);
    try {
      const response = await setCCOrderImbalanceConfig(config.rows);
      toast.success(response.msg);
      setDirty(false);
      setIsOpen(false);
    } catch (error) {
      packetErrorHandler(error);
    } finally {
      setSaving(false);
    }
  };

  const disabled = !config?.canManage || saving;
  const valid = config?.rows.every((row) => row.stages.every((stage) =>
    (stage.waitSeconds === null || (Number.isInteger(stage.waitSeconds) && stage.waitSeconds >= 0 &&
    stage.waitSeconds <= 2147483647)) &&
    (stage.tolerancePercent === null || (Number.isFinite(stage.tolerancePercent) && stage.tolerancePercent >= 0)),
  ));

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="flex min-w-0 w-full items-center gap-2 rounded border-2 border-gray-300 px-2 py-3 text-xs transition-all hover:border-gray-500"
      >
        <SettingOutlined aria-hidden="true" />
        <span>تنظیمات رفع ناترازی</span>
      </button>
      {isOpen ? (
        <Modal
          onClose={() => { if (!saving) setIsOpen(false); }}
          modalBoxClassName="!w-[1120px] !min-w-0 !max-w-[95vw]"
          UI={
            <form dir="rtl" onSubmit={save} className="flex w-full flex-col gap-4" aria-label="تنظیمات رفع ناترازی">
              <div>
                <h2 className="text-base font-bold">تنظیمات رفع ناترازی</h2>
                <p className="mt-2 text-xs leading-6 text-gray-500">
                  تنظیمات هر مدل کاوردکال و نوع هدف برای همهٔ سفارش‌های همان مدل و هدف مشترک است.
                </p>
              </div>
              {loading ? (
                <div role="status" className="py-16 text-center text-sm text-gray-500">در حال دریافت تنظیمات...</div>
              ) : loadFailed ? (
                <div role="alert" className="flex flex-col items-center gap-4 py-12 text-sm text-gray-600">
                  دریافت تنظیمات انجام نشد.
                  <button type="button" onClick={() => setReloadKey((key) => key + 1)} className="rounded border border-gray-300 px-4 py-2">تلاش دوباره</button>
                </div>
              ) : config ? (
                <>
                  <div className="overflow-x-auto rounded border border-gray-200">
                    <table className="w-full min-w-[960px] border-collapse text-xs">
                      <caption className="sr-only">زمان انتظار و درصد آسان‌گیری در سه مرحله، به تفکیک مدل کاوردکال و نوع هدف</caption>
                      <thead className="bg-gray-100 text-gray-700">
                        <tr>
                          <th scope="col" rowSpan={2} className="w-16 border-b border-l border-gray-200 px-3 py-3">مدل</th>
                          <th scope="col" rowSpan={2} className="w-[270px] border-b border-l border-gray-200 px-3 py-3">نوع هدف</th>
                          {stageTitles.map((title) => (
                            <th key={title} scope="colgroup" colSpan={2} className="border-b border-l border-gray-200 py-3 text-center">{title}</th>
                          ))}
                        </tr>
                        <tr>
                          {stageTitles.map((title) => (
                            <Fragment key={title}>
                              <th scope="col" className="border-b border-gray-200 px-2 py-3 font-medium">انتظار (ثانیه)</th>
                              <th scope="col" className="border-b border-l border-gray-200 px-2 py-3 font-medium">آسان‌گیری (%)</th>
                            </Fragment>
                          ))}
                        </tr>
                      </thead>
                      {models.map((model) => (
                        <tbody key={model.id} className="border-b border-gray-200 last:border-b-0">
                          {config.rows.filter((row) => row.orderType === model.id).map((row, rowIndex, rows) => {
                            const criterionName = getCCOrderCriterionTypeName(null, model.id, row.criterionType);
                            return (
                              <tr key={row.criterionType} className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50">
                                {rowIndex === 0 ? <th scope="rowgroup" rowSpan={rows.length} className="border-l border-gray-200 bg-gray-50 px-3 py-3 text-center font-bold">{model.title}</th> : null}
                                <th scope="row" className="border-l border-gray-200 px-3 py-3 text-right font-normal leading-6">{criterionName}</th>
                                {row.stages.map((stage, stageIndex) => {
                                  const inactive = !stage.waitSeconds;
                                  return (
                                    <Fragment key={stageIndex}>
                                      {(["waitSeconds", "tolerancePercent"] as const).map((field) => (
                                        <td key={field} className={`px-2 py-3 ${field === "tolerancePercent" ? "border-l border-gray-200" : ""}`}>
                                          <input
                                            type="number"
                                            inputMode={field === "waitSeconds" ? "numeric" : "decimal"}
                                            min={0}
                                            max={field === "waitSeconds" ? 2147483647 : undefined}
                                            step={field === "waitSeconds" ? 1 : "any"}
                                            disabled={disabled}
                                            value={Number.isFinite(stage[field]) ? stage[field] ?? "" : ""}
                                            onChange={(event) => changeStage(row, stageIndex, field, event.target.value)}
                                            aria-label={`${model.title}، ${criterionName}، ${stageTitles[stageIndex]}، ${field === "waitSeconds" ? "زمان انتظار" : "درصد آسان‌گیری"}`}
                                            title={inactive ? "این مرحله تنظیم نشده است؛ زمان انتظار صفر یا خالی است." : "مرحله فعال است"}
                                            dir="ltr"
                                            className={`h-9 w-full min-w-[80px] rounded border border-gray-300 px-2 text-center outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 disabled:cursor-not-allowed ${inactive ? "bg-gray-50 text-gray-500" : "bg-white text-gray-900"}`}
                                          />
                                        </td>
                                      ))}
                                    </Fragment>
                                  );
                                })}
                              </tr>
                            );
                          })}
                        </tbody>
                      ))}
                    </table>
                  </div>
                  <div className="text-xs leading-6 text-gray-500">
                    <p>زمان انتظار صفر یا خالی یعنی این مرحله تنظیم نشده و اجرا نمی‌شود؛ درصد صفر یا خالی، هدف اولیه را حفظ می‌کند.</p>
                    <p>درصد آسان‌گیری هر مرحله نسبت به هدف اولیهٔ سفارش محاسبه می‌شود.</p>
                  </div>
                  <div className="flex items-center justify-end gap-3 border-t border-gray-100 pt-4">
                    {!config.canManage ? <span className="ml-auto text-xs text-gray-500">تغییر تنظیمات مشترک فقط برای مدیر مجاز است.</span> : null}
                    <button type="button" disabled={saving} onClick={() => setIsOpen(false)} className="h-9 rounded border border-gray-300 px-6 text-xs disabled:opacity-50">بستن</button>
                    {config.canManage ? <button type="submit" disabled={saving || !dirty || !valid} className="h-9 rounded bg-sky-600 px-6 text-xs text-white disabled:opacity-50">{saving ? "در حال ذخیره..." : "ذخیره تنظیمات"}</button> : null}
                  </div>
                </>
              ) : null}
            </form>
          }
        />
      ) : null}
    </>
  );
};

export default ImbalanceSetting;
